import React, { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import {
  Box,
  Checkbox,
  Collapse,
  Icon,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { TextFormattedNumber, DesignedConfirmDialog } from 'components';
import logo from 'components/Logo/logo-dermpro.svg';
import { imgBaseUrl } from 'configs';
import { singleUnitName } from 'helpers/other';
import round from 'helpers/mathRound';
import { setSelectedDraftedQuote } from 'redux/actions/quote';
import { cancelQuoteApi } from 'api';
import { getAlleDiscount, statusActivityNames } from 'pages/quotes/helpers';
import { getDiscount } from 'pages/quotes/utils/getDiscount';
import { extractAttributesValues } from 'pages/quotes/utils/extractAttributesValues';
import { useStyles } from '../styles';


const attributesToFind = [
  'short_description',
  'image',
  'subscription_active',
  'catalog_product_unit',
  'savings',
  'subscription_label',
  'additional_subscription_price',
  'quotepro_discounts'
];

const RecentQuotesTableRow = ({ quote, headTableCells }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [loaded, setLoaded] = useState(false);
  const [openRow, setOpenRow] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  const { allQuoteProducts, selectedDraftedQuote } = useSelector((state: IReduxState) => state.quote);
  const { profile: { stores } } = useSelector((state: IReduxState) => state.user);
  const [{ apiUrl }] = stores;

  const {
    id,
    increment_id,
    customer_name,
    customer_email,
    created_at,
    created_by_doctor,
    status = '',
    discount_price,
    discount_price_type,
    items,
    start_date,
    expired_date
  } = quote || {};

  const itemCreatedBy = created_by_doctor || 'N/A';
  const quoteDateCreated = created_at ? format(new Date(created_at), 'MM/dd/yyyy') : '';
  const quoteDateStart = start_date ? format(new Date(start_date), 'MM/dd/yyyy') : '';
  const quoteDateExpire = expired_date ? format(new Date(expired_date), 'MM/dd/yyyy') : '';
  const quoteStatus = status ? statusActivityNames[status] : statusActivityNames['7'];
  const mediaStoreUrl = `${apiUrl}/media/catalog/product` || imgBaseUrl;

  const columns = headTableCells.reduce((acc, column) => {
    acc[column.label] = column;
    return acc;
  }, {});

  let total = 0;
  let quoteItemsDiscount = 0;
  let savingsValue = 0;

  items.forEach((item) => {
    const { qty, price, discount_price, discount_price_type, sku } = item;
    const productInfo = allQuoteProducts.find((product) => product.sku === sku);
    const attributesValues = productInfo?.custom_attributes ?
      extractAttributesValues(productInfo?.custom_attributes, attributesToFind) : {};

    const { savings = 0 } = attributesValues;
    savingsValue += round(Number(savings) * qty) || 0;
    const isAlleSKU = sku.includes('bdsku');

    const subtotal = isAlleSKU ? -round(getAlleDiscount(productInfo)) || 0 : qty * price;
    total += subtotal;

    if (discount_price_type === 'fixed') {
      quoteItemsDiscount += discount_price * qty;
    } else if (discount_price_type === 'percent') {
      quoteItemsDiscount += subtotal * discount_price / 100;
    }
  });

  const quoteDiscount = discount_price_type === 'fixed' ?
    discount_price :
    total * discount_price / 100;

  const totalDiscount = quoteItemsDiscount + savingsValue + quoteDiscount;
  total -= totalDiscount;

  const handleItemSelect = (itemId) => {
    if (itemId === selectedDraftedQuote?._id) {
      dispatch(setSelectedDraftedQuote({}));
    } else {
      dispatch(setSelectedDraftedQuote({
        _id: itemId
      }));
    }
  };

  return (
    <Fragment>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            checked={increment_id === selectedDraftedQuote?._id}
            color="primary"
            onChange={() => handleItemSelect(increment_id)}
          />
        </TableCell>
        {columns['Quote ID']?.display && <TableCell align="center">{increment_id}</TableCell>}
        {columns['Customer Name']?.display && <TableCell align="center">{customer_name}</TableCell>}
        {columns['Customer Email']?.display && <TableCell align="center">{customer_email}</TableCell>}
        {columns['Created By']?.display && <TableCell className="whitespace-no-wrap" align="center">{itemCreatedBy}</TableCell>}
        {columns['Date Created']?.display &&
          <TableCell align="center">
            {quoteDateCreated}
          </TableCell>
        }
        {columns['Date Start']?.display &&
          <TableCell align="center">
            {quoteDateStart}
          </TableCell>
        }
        {columns['Date Expire']?.display &&
          <TableCell align="center">
            {quoteDateExpire}
          </TableCell>
        }
        {columns['Quote Discount']?.display &&
        <TableCell align="center">
          <NumberFormat
            value={quoteDiscount}
            displayType="text"
            thousandSeparator={','}
            decimalSeparator={'.'}
            prefix={'$'}
            fixedDecimalScale
            decimalScale={2}
          />
        </TableCell>
        }
        {columns['Total']?.display &&
        <TableCell align="center">
          <NumberFormat
            value={total}
            displayType="text"
            thousandSeparator={','}
            decimalSeparator={'.'}
            prefix={'$'}
            fixedDecimalScale
            decimalScale={2}
          />
        </TableCell>
        }
        {columns['Status']?.display &&
        <TableCell align="center">
          <span className={`whitespace-no-wrap ${status} ${status ? 'indicator' : null}`}>
            {quoteStatus?.title}
          </span>
        </TableCell>
        }
        <TableCell align="center">
          <div className="flex items-center justify-center">
            <IconButton
              title="Cancel Quote"
              disabled={['3', '4', '5'].includes(String(status))}
              onClick={() => setOpenCancelDialog(true)}
            >
              <Icon>highlight_off</Icon>
            </IconButton>
            <DesignedConfirmDialog
              title="Cancel Quote"
              open={openCancelDialog}
              setOpen={setOpenCancelDialog}
              onConfirm={() => cancelQuoteApi({ dispatch, quoteId: id })}
            >
              Are you sure you want to cancel this quote?
            </DesignedConfirmDialog>
            <IconButton
              title={openRow ?  'Show Less' : 'Show More'}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setOpenRow(!openRow);
              }}
            >
              <Icon>{openRow ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}</Icon>
            </IconButton>
          </div>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell className="p-0" colSpan={12}>
          <Collapse in={openRow} timeout="auto" unmountOnExit>
            <Box margin={0}>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell width="80px">ITEMS</TableCell>
                    <TableCell width="30%" />
                    <TableCell align="center">QUANTITY</TableCell>
                    <TableCell align="center">SUBTOTAL</TableCell>
                    <TableCell align="center">SAVINGS</TableCell>
                    <TableCell align="center">TOTAL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => {
                    const productInfo = allQuoteProducts.find((product) => product.sku === item.sku);

                    if (!productInfo) {
                      return null;
                    }

                    const {
                      name,
                      price,
                      custom_attributes,
                      status
                    } = productInfo;

                    const attributesValues = custom_attributes ?
                      extractAttributesValues(custom_attributes, attributesToFind) : {};

                    const {
                      image,
                      catalog_product_unit = 'units',
                      savings,
                      short_description
                    } = attributesValues;

                    const {
                      sku,
                      qty,
                      price: updatedPrice,
                      discount_price,
                      discount_price_type,
                      item_id,
                      savings: savingsByCreating
                    } = item;

                    const quotepro_discounts = {
                      price_type: discount_price_type,
                      price: discount_price
                    };

                    const itemPrice = updatedPrice || price || 0;
                    const savingsValue = round(Number(savingsByCreating)) || round(Number(savings)) || 0;
                    const unitsName = catalog_product_unit || 'units';
                    const isAlleSKU = sku.includes('bdsku');

                    const dataForDiscount = {
                      savings: savingsValue,
                      discount_options: [quotepro_discounts],
                      itemPrice
                    };

                    const itemModified = {
                      qty,
                      product_option: {
                        extension_attributes: { quotepro_discounts }
                      },
                      price
                    };

                    const discount = isAlleSKU ?
                      round(getAlleDiscount(productInfo)) || 0 :
                      round(getDiscount(itemModified, dataForDiscount)) || 0;

                    const subtotal = itemPrice * qty || 0;
                    const amount = savingsValue && !updatedPrice ?
                      itemPrice * qty :
                      (itemPrice * qty) - discount;

                    return (
                      <TableRow key={`${index}-${increment_id}-${item_id}-${sku}`} className={status != 1 ? classes.inactiveItem : ''}>
                        <TableCell component="th" scope="row">
                          <img
                            src={loaded ? `${mediaStoreUrl}${image}` : logo}
                            alt={`${name} image`}
                            className={classes.patientActivityTableImage}
                            onLoad={() => setLoaded(true)}
                            onError={(e) => {
                              (e.target as HTMLImageElement).onerror = null;
                              (e.target as HTMLImageElement).src = logo;
                            }}
                          />
                        </TableCell>
                        <TableCell width="50%">
                          <Box className={classes.nameContainer}>
                            <Typography variant="subtitle2" className="font-semibold" gutterBottom>
                              {name}
                            </Typography>
                            {status != 1 && (
                              <Box component="span" className={classes.badge}>Inactive</Box>
                            )}
                          </Box>
                          <span className="text-13 font-italic font-medium whitespace-no-wrap">
                            (<TextFormattedNumber value={round(updatedPrice ? updatedPrice : itemPrice - savingsValue)} prefix="$" />/
                            {singleUnitName(unitsName)})
                          </span>
                          <Typography dangerouslySetInnerHTML={{ __html: short_description || '' }} />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{`${qty} ${
                            qty === 1 ? singleUnitName(unitsName) : unitsName
                          }`}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <TextFormattedNumber value={subtotal} prefix="$" />
                        </TableCell>
                        <TableCell align="center">
                          <TextFormattedNumber value={discount} prefix="$" />
                        </TableCell>
                        <TableCell align="center">
                          <TextFormattedNumber value={isAlleSKU ? 0 : amount} prefix="$" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export default RecentQuotesTableRow;
